import { User } from "firebase/auth";
import * as z from "zod";

export enum Roles {
	ADMIN = "ADMIN",
	ISSUING_DOCTOR = "ISSUING_DOCTOR",
	RECOMMENDING_DOCTOR = "RECOMMENDING_DOCTOR",
	BROKER = "BROKER",
}
export type TUser = User & {
	admin?: boolean;
	issuingDoctor?: boolean;
	recommendingDoctor?: boolean;
	broker?: boolean;
};

// either one word or 2 words that the first is no longer than 6
export const NameSchema = z
	.string()
	.min(2, "השדה חייב להכיל לפחות 2 תווים")
	.max(20, { message: "השדה לא יכול להכיל יותר מ 20 תווים" });

// Shared regex
const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

// Required phone (must be valid and not empty)
export const PhoneSchema = z
	.string({ required_error: "יש להזין מספר טלפון" })
	.min(1, "יש להזין מספר טלפון")
	.regex(phoneRegex, "מספר הטלפון לא תקין");

// Optional phone (can be empty/undefined/null, but must be valid if provided)
export const OptionalPhoneSchema = z
	.string()
	.optional()
	.refine((val) => !val || phoneRegex.test(val), { message: "מספר הטלפון לא תקין" });

export const UserIdNumberSchema = z
	.string({
		errorMap: () => ({ message: "יש להזין תעודת זהות" }),
	})
	.refine(
		(id) => {
			if (!id || !Number(id) || id.length !== 9 || isNaN(Number(id))) {
				// Make sure ID is formatted properly
				return false;
			}
			let sum = 0,
				incNum;
			for (let i = 0; i < id.length; i++) {
				incNum = Number(id[i]) * ((i % 2) + 1); // Multiply number by 1 or 2
				sum += incNum > 9 ? incNum - 9 : incNum; // Sum the digits up and add to total
			}
			return sum % 10 === 0;
		},
		{
			message: "תעודת זהות לא תקינה",
		}
	);

export const PasswordSchema = z
	.string()
	.min(6, {
		message: "הסיסמא חייבת להכיל לפחות 6 ספרות",
	})
	.max(16, { message: "הסיסמא יכולה להכיל לכל היותר 16 ספרות" });

const REQUEST_STATUSES = [
	"rejected",
	"awaiting_completion",
	"approved",
	"needs_additional_info",
	"waiting_paymnet",
	"pending_doctor_approval",
	"pending_admin_approval",
	"missing_files",
	"handeled_by_admin",
	"rejected_by_admin",
] as const;

interface RequestData {
	request_id: string;
	request_type: "new" | "renew" | "increase" | null;
	user_id: string;
	first_name: string;
	last_name: string;
	email: string;
	phone: string | null;
	phone2: string | null;
	dob: Date | null;
	request_status: (typeof REQUEST_STATUSES)[number];
	sex: "male" | "female" | "other" | undefined | null;
	bituah_provider:
		| "other"
		| "clalit"
		| "maccabi"
		| "leumit"
		| "meuhedet"
		| "missing"
		| undefined
		| null;
	city: string | null;
	street: string | null;
	house_number: string | null;
	apartment_number: string | null;
	psychiatric_treatment: boolean | null;
	addiction_history: boolean | null;
	high_blood_sugar: boolean | null;
	high_blood_pressure: boolean | null;
	has_heart_issues: boolean | null;
	has_weapon_license: boolean | null;
	transporters: { id: string; name: string }[] | null;
	dosage:
		| {
				type: string;
				amount: number;
				centralism: string;
		  }[]
		| null;
	user_comment: string;
}

export { REQUEST_STATUSES };
export type { RequestData };
